import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import
  {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    IconButton,
    Typography,
    Avatar,
    Menu,
    MenuItem,
    Stack,
    Button,
    Tooltip,
    Chip,
  } from '@mui/material';
import { styled } from '@mui/material/styles';
import { MoreVert as MoreVertIcon, Inventory2Outlined } from '@mui/icons-material';

import { createBuild } from '../../redux-store/testSuiteBuildReducers/testSuiteBuildActions';
import { setSelectedTestSuites } from "../../redux-store/testSuiteReducers/testSuiteSlice";
import CustomDialog from '../Common/CustomDialog';
import DeleteDialog from '../Common/DeleteDialog';
import CustomChip from '../Common/CustomChip';
import { STATUS_TO_ICON, BUILD_STATUS_TO_COLOR } from '../../config/constants';

// Styled components from TestScenariosList
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1, 1.5),
  borderBottom: '1px solid var(--borderColor)',
  color: 'var(--text-primary)',
  '&.checkbox-cell': {
    width: '32px',
    padding: theme.spacing(0.5),
  },
  '&.date-cell': {
    width: '140px',
    padding: theme.spacing(1, 1.5),
    whiteSpace: 'nowrap',
  },
  '&.action-cell': {
    width: '48px',
    padding: theme.spacing(0.5),
  }
}));

const StyledTableHeaderCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1, 1.5),
  backgroundColor: 'var(--background-primary)',
  fontWeight: 600,
  fontSize: '1rem',
  color: 'var(--text-primary)',
  borderBottom: '2px solid var(--borderColor)',
  '&.checkbox-cell': {
    width: '32px',
    padding: theme.spacing(0.5),
  },
  '&.date-cell': {
    width: '140px',
    padding: theme.spacing(1, 1.5),
  },
  '&.action-cell': {
    width: '48px',
    padding: theme.spacing(0.5),
  }
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: 32,
  height: 32,
  backgroundColor: 'var(--agent-avatar)',
  color: 'var(--text-primary)',
  fontSize: '0.875rem',
  fontWeight: 500,
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const colors = {
    active: theme.palette.success.main,
    draft: theme.palette.warning.main,
    archived: 'var(--text-tertiary)',
    failed: theme.palette.error.main,
    running: theme.palette.info.main,
  };
  return {
    color: colors[status?.toLowerCase()] || 'var(--text-tertiary)',
    backgroundColor: 'transparent',
    borderColor: colors[status?.toLowerCase()] || 'var(--text-tertiary)',
    '& .MuiChip-label': {
      fontWeight: 500,
    },
  };
});

const TestSuitesList = function ({
  testSuiteList,
  pageNumber,
  onDelete,
  onArchive,
  onMarkActive
})
{
  const isLoading = useSelector((state) => state.testSuite.isLoading);
  const history = useHistory();
  const dispatch = useDispatch();
  const environmentSets = useSelector((state) => state.variableSet.allVariableSets);

  const [checkedTestSuites, setCheckedTestSuites] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTestSuiteId, setDeleteTestSuiteId] = useState('');
  const [buildSuiteId, setBuildSuiteId] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuId, setMenuId] = useState(null);

  const allChecked = testSuiteList.filter((suite) => suite.status === "active").length === checkedTestSuites.length && checkedTestSuites.length !== 0;

  useEffect(() =>
  {
    updateCheckedTestSuitesIds([]);
  }, [testSuiteList]);

  const handleMenuClick = (event, id) =>
  {
    setAnchorEl(event.currentTarget);
    setMenuId(id);
  };

  const handleMenuClose = () =>
  {
    setAnchorEl(null);
    setMenuId(null);
  };

  const handleAllCheckboxChange = () =>
  {
    if (allChecked)
    {
      updateCheckedTestSuitesIds([]);
    } else
    {
      updateCheckedTestSuitesIds(
        testSuiteList
          .filter((suite) => suite.status === "active")
          .map((suite) => suite.id)
      );
    }
  };

  const handleCheckboxChange = (e, tId) =>
  {
    if (e.target.checked)
    {
      updateCheckedTestSuitesIds([...checkedTestSuites, tId]);
    } else
    {
      updateCheckedTestSuitesIds(checkedTestSuites.filter((id) => id !== tId));
    }
  };

  const updateCheckedTestSuitesIds = async (suiteIds) =>
  {
    setCheckedTestSuites(suiteIds);
    const selectedTestSuites = testSuiteList
      .filter((suite) => suiteIds.indexOf(suite.id) !== -1)
      .map((suite) => ({
        title: suite.title,
        id: suite.id,
      }));
    await dispatch(setSelectedTestSuites([...selectedTestSuites]));
  };

  const onViewTestSuite = (id) =>
  {
    history.push(`/test-suites/${id}/?page=${pageNumber}`);
  };

  const onBuild = async (envId) =>
  {
    const buildResponse = await dispatch(
      createBuild({ testSuiteId: buildSuiteId, environmentSetId: envId })
    );
    history.push(`/test-suite-builds/${buildResponse.payload.id}`);
    setBuildSuiteId('');
  };

  const onViewBuilds = (id) =>
  {
    history.push(`/test-suites/${id}/build/`);
    handleMenuClose();
  };

  const onClickDelete = (id) =>
  {
    setDeleteTestSuiteId(id);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const onDeleteConfirm = () =>
  {
    onDelete(deleteTestSuiteId);
    setDeleteDialogOpen(false);
    setDeleteTestSuiteId('');
  };

  const onArchiveConfirm = () =>
  {
    onArchive(deleteTestSuiteId);
    setDeleteDialogOpen(false);
    setDeleteTestSuiteId('');
  };

  const styles = {
    paper: {
      backgroundColor: 'var(--background-primary)',
      color: 'var(--text-primary)',
    },
    tableRow: {
      '&:hover': {
        backgroundColor: 'var(--hover-bg)',
      },
      '&:last-child td, &:last-child th': {
        borderBottom: '1px solid var(--borderColor)',
      },
    },
    description: {
      color: 'var(--text-secondary)',
    },
    menu: {
      '& .MuiPaper-root': {
        backgroundColor: 'var(--background-primary)',
        color: 'var(--text-primary)',
        border: '1px solid var(--borderColor)',
      },
      '& .MuiMenuItem-root': {
        fontSize: '0.95rem',
        padding: '8px 16px',
        color: 'var(--text-primary)',
        '&:hover': {
          backgroundColor: 'var(--hover-bg)',
        },
      },
    },
    iconButton: {
      color: 'var(--text-secondary)',
      '&:hover': {
        backgroundColor: 'var(--hover-bg)',
      },
    },
    checkbox: {
      color: 'var(--text-secondary)',
      '&.Mui-checked': {
        color: 'var(--primary-color)',
      },
    },
  };

  const renderTable = () =>
  {
    if (testSuiteList.length === 0)
    {
      return (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" sx={{ color: 'var(--text-secondary)' }}>
            No results found.
          </Typography>
        </Box>
      );
    }

    return (
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableHeaderCell padding="checkbox" className="checkbox-cell">
                <Checkbox
                  checked={allChecked}
                  onChange={handleAllCheckboxChange}
                  disabled={testSuiteList?.filter((suite) => suite.status === "active").length === 0}
                  sx={styles.checkbox}
                />
              </StyledTableHeaderCell>
              <StyledTableHeaderCell>Details</StyledTableHeaderCell>
              <StyledTableHeaderCell className="date-cell">Created At</StyledTableHeaderCell>
              <StyledTableHeaderCell>Status</StyledTableHeaderCell>
              <StyledTableHeaderCell>Tags</StyledTableHeaderCell>
              <StyledTableHeaderCell>Owner</StyledTableHeaderCell>
              <StyledTableHeaderCell>Last Run Result</StyledTableHeaderCell>
              <StyledTableHeaderCell className="action-cell" align="center">Action</StyledTableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {testSuiteList.map(({
              id,
              code,
              title,
              description,
              status,
              tags,
              owner,
              test_suite_statuses,
              created_at
            }) => (
              <TableRow
                key={id}
                hover
                sx={{
                  ...styles.tableRow,
                  backgroundColor: "var(--background-primary)"
                }}
              >
                <StyledTableCell padding="checkbox" className="checkbox-cell">
                  <Checkbox
                    checked={checkedTestSuites.includes(id)}
                    onChange={(e) => handleCheckboxChange(e, id)}
                    disabled={status !== "active"}
                    sx={styles.checkbox}
                  />
                </StyledTableCell>

                <StyledTableCell
                  onClick={() => onViewTestSuite(id)}
                  sx={{ cursor: 'pointer', width: '300px' }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        lineHeight: 1.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        color: 'var(--text-primary)',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontWeight: 600,
                          marginRight: 1,
                          whiteSpace: 'nowrap',
                          display: 'inline-block',
                          minWidth: 'fit-content'
                        }}
                      >
                        {code}:
                      </Box>
                      {title}
                    </Typography>
                    {description && (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: 'vertical',
                          color: 'var(--text-secondary)',
                        }}
                      >
                        {description}
                      </Typography>
                    )}
                  </Box>
                </StyledTableCell>

                <StyledTableCell className="date-cell">
                  <Typography
                    variant="body2"
                    sx={{
                      width: '100%',
                      textAlign: 'left',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    {dayjs(created_at * 1000).format('D MMM, YYYY HH:mm')}
                  </Typography>
                </StyledTableCell>

                <StyledTableCell>
                  <StatusChip
                    label={status}
                    status={status}
                    variant="outlined"
                    size="small"
                  />
                </StyledTableCell>

                <StyledTableCell>
                  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                    {(tags || []).map((tag) => (
                      <CustomChip key={tag} label={tag} />
                    ))}
                  </Stack>
                </StyledTableCell>

                <StyledTableCell>
                  {owner && (
                    <Tooltip
                      title={owner.name}
                      arrow
                      placement="top"
                    >
                      <UserAvatar>{owner.name.split(' ')
                        .map(word => word[0])
                        .join('')
                        .toUpperCase()}</UserAvatar>
                    </Tooltip>
                  )}
                </StyledTableCell>

                <StyledTableCell>
                  <Stack spacing={0.5}>
                    {(test_suite_statuses || []).map(({
                      environment_set: env,
                      status: buildStatus,
                      last_run_at: lastRunAt,
                    }, i) => (
                      <Typography
                        key={env?.id || `build-status-${i}`}
                        variant="body2"
                        component="div"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <Box component="span" sx={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                          {env?.title || '-'}
                        </Box>
                        :
                        <Box
                          component="span"
                          sx={{ color: BUILD_STATUS_TO_COLOR[buildStatus] || 'var(--text-secondary)' }}
                        >
                          {lastRunAt ? dayjs(lastRunAt * 1000).fromNow() : buildStatus || '-'}
                        </Box>
                      </Typography>
                    ))}
                  </Stack>
                </StyledTableCell>

                <StyledTableCell className="action-cell" align="center">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuClick(e, id)}
                    sx={styles.iconButton}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </StyledTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Paper elevation={1} sx={styles.paper}>
      {isLoading ? null : renderTable()}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={styles.menu}
      >
        {(() =>
        {
          const status = testSuiteList.find(t => t.id === menuId)?.status;
          return status === 'active' && (
            <MenuItem onClick={() =>
            {
              setBuildSuiteId(menuId);
              handleMenuClose();
            }}>
              Run
            </MenuItem>
          );
        })()}
        <MenuItem onClick={() => onViewBuilds(menuId)}>
          All builds
        </MenuItem>
        {(() =>
        {
          const status = testSuiteList.find(t => t.id === menuId)?.status;
          return (status === 'draft' || status === 'archived') && (
            <MenuItem onClick={() =>
            {
              onMarkActive(menuId);
              handleMenuClose();
            }}>
              Mark as Active
            </MenuItem>
          );
        })()}
        <MenuItem
          onClick={() => onClickDelete(menuId)}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
      </Menu>

      <DeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="Delete Test Suite? Archive instead!"
        deleteBtnLabel="Delete Permanently"
        secondaryBtnLabel="Archive"
        secondaryBtnIcon={<Inventory2Outlined />}
        onDeleteAction={onDeleteConfirm}
        onSecondaryAction={onArchiveConfirm}
      >
        <Typography variant="body1" sx={{ mb: 2, color: 'var(--text-primary)' }}>
          We recommend archiving Test Suites instead of deleting.
        </Typography>
        <Typography variant="body1" sx={{ color: 'var(--text-primary)' }}>
          Are you sure you want to permanently delete this Test Suite?
        </Typography>
      </DeleteDialog>

      <CustomDialog
        open={!!buildSuiteId}
        title="Select environment"
        btnText="Run"
        onClose={() => setBuildSuiteId('')}
        options={environmentSets || []}
        handleSubmit={(env) => onBuild(env)}
      />
    </Paper>
  );
};

export default TestSuitesList;